// Which cells a bulk crew setup writes, and which it clears.
//
// Lives in lib/ with no runtime imports on purpose: it is the destructive half
// of the setup dialog, and keeping it free of app wiring means it can be tested
// directly. (Importing it from the component would drag in apiClient → MSAL
// config, which needs a browser env.)
import type { CrewPhase } from './scheduleModel';

/** A cell key: `YYYY-MM-DD|phase`, phase empty for a non-fab job's single row. */
export const cellKey = (workDate: string, phase: CrewPhase | null) => `${workDate}|${phase ?? ''}`;

export interface SetupPlan {
  /** Cells the setup writes a crew figure to. */
  written: Set<string>;
  /** Future cells it blanks: already had crew, new pattern doesn't cover them. */
  cleared: string[];
  /** Written cells that already had crew — replaced rather than newly filled. */
  replaced: number;
  /** The working days actually written to — the caller's `days` with anything
   *  before today dropped. Drives both the entries and the day count shown. */
  effectiveDays: string[];
  /** Working days discarded for being in the past. Lets the dialog say so
   *  rather than silently writing fewer days than the range implies. */
  skippedPastDays: number;
}

/**
 * Work out exactly which cells a setup writes and which it clears.
 *
 * Three rules carry all the risk here:
 *
 *  1. THE PAST IS UNTOUCHABLE — not cleared, and not written either. Those days
 *     have been worked; the schedule is the record of what was planned at the
 *     time. (product owner, 2026-07-28: "clear anything that hasn't already occurred",
 *     then confirmed writing should start at today too.) Same rule the "Remove
 *     from Manpower" button already follows.
 *
 *     Writing matters as much as clearing here, because the dialog DEFAULTS its
 *     range to the job's phase span — so a job that started in June opens with a
 *     June start date, and without this the setup would overwrite months of
 *     worked history with today's crew figure.
 *
 *  2. Only rows the scheduler actually entered a figure for are touched. On a
 *     fab job, setting up fabrication alone must leave installation's future
 *     alone — clearing a row the user never filled in would be indefensible.
 *
 *  3. A cell is never both written and cleared; writing wins, and it counts as
 *     "replaced" instead.
 */
export function planSetupCells(opts: {
  /** Working days the setup writes to, YYYY-MM-DD. */
  days: string[];
  /** Phases being set up — one entry per row the scheduler filled in. */
  enteredPhases: (CrewPhase | null)[];
  /** Existing crew for the job, from the lookup window. */
  existing: { workDate: string; phase: string | null }[];
  /** YYYY-MM-DD boundary; anything strictly before is untouchable. */
  today: string;
  /** Fab jobs render legacy NULL-phase entries on the fabrication line. */
  isFab: boolean;
}): SetupPlan {
  const { days, enteredPhases, existing, today, isFab } = opts;

  // Rule 1, applied here rather than at the call site so writing and clearing
  // can never disagree about where "the past" starts.
  const effectiveDays = days.filter((ds) => ds >= today);
  const skippedPastDays = days.length - effectiveDays.length;

  const written = new Set<string>();
  for (const phase of enteredPhases) {
    for (const ds of effectiveDays) written.add(cellKey(ds, phase));
  }

  const targets = new Set(enteredPhases);
  const future = new Set<string>();
  for (const e of existing) {
    if (e.workDate < today) continue;                       // rule 1
    const phase = (e.phase ?? (isFab ? 'fabrication' : null)) as CrewPhase | null;
    if (!targets.has(phase)) continue;                      // rule 2
    future.add(cellKey(e.workDate, phase));
  }

  return {
    written,
    cleared: [...future].filter((k) => !written.has(k)).sort(),
    replaced: [...written].filter((k) => future.has(k)).length,
    effectiveDays,
    skippedPastDays,
  };
}
